/**
 * Auth/session calls the SPA makes around sign-in, onboarding and sign-out — the one place for
 * them (they used to be copy-pasted across ~9 pages). All go through the global apiClient and
 * NEVER throw: every caller treats these as best-effort or branches on the result.
 *
 * All are `silent`: they run on sign-in / onboarding screens where apiClient's generic toasts
 * ("Session expired — refresh", "Too many requests") would be confusing mid-login. Callers show
 * their own copy (or nothing, as before).
 *
 * These endpoints don't enforce CSRF (session-sync checks cross-origin; the session cookies are
 * SameSite=Lax), but apiClient attaches the token anyway — harmless, and it keeps them
 * forward-compatible if requireCsrf is added server-side.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { api, type ApiResponse } from '@/lib/apiClient';
import { purgeServerDataMirror } from '@/lib/localDataMirror';
import { isMockMode } from '../../utils/env';

/**
 * Bridge a freshly-issued Supabase session from the browser's localStorage into the Worker's
 * httpOnly cookie jar so subsequent `/api/*` calls are authenticated.
 *
 * Always `await` it BEFORE redirecting: if it hasn't resolved before `window.location.replace`,
 * the first post-redirect Worker call can arrive anonymous and the user sees a flash of "please
 * sign in" or a hard 401.
 *
 * Returns true on a 2xx, false for missing tokens, any network failure or non-2xx status.
 */
export async function syncSessionToServer(tokens: {
  access_token: string | null | undefined;
  refresh_token: string | null | undefined;
}): Promise<boolean> {
  if (!tokens.access_token || !tokens.refresh_token) return false;
  const res = await api.post(
    '/api/auth/session-sync',
    { access_token: tokens.access_token, refresh_token: tokens.refresh_token },
    { envelope: false, silent: true },
  );
  return res.ok;
}

/** Read the browser Supabase client's current session and sync it (false if there is none). */
export async function syncSupabaseSession(sb: SupabaseClient | null | undefined): Promise<boolean> {
  if (!sb) return false;
  try {
    const { data } = await sb.auth.getSession();
    const session = data.session;
    if (!session) return false;
    return await syncSessionToServer(session);
  } catch {
    // getSession can throw on corrupt storage; treat as "nothing to sync".
    return false;
  }
}

/**
 * Clear the Worker's httpOnly auth cookies (POST — GET /api/auth/signout isn't served in
 * Vite-only dev). Best-effort: callers always go on to clear the client session and redirect.
 *
 * Also purges client/nutrition data earlier builds mirrored into localStorage. It lives here
 * because every sign-out path (Sidebar, provider signOut, onboarding, sign-in pages) calls this.
 * Skipped in mock mode, where those keys are the local fake backend.
 */
export async function signOutOnServer(): Promise<void> {
  if (!isMockMode()) purgeServerDataMirror();
  await api.post('/api/auth/signout', undefined, { envelope: false, silent: true });
}

/**
 * Exchange an invite token + email for the invite id (client signup path). Non-throwing; success
 * carries `inviteId`, failure the server's `message`, and `status` is absent when the request
 * never reached the server.
 */
export function redeemInvite(input: {
  token: string;
  email: string;
  userId: string | undefined;
}): Promise<ApiResponse & { inviteId?: string }> {
  return api.post('/api/invites/redeem', input, { silent: true });
}
